<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎯 Pitch Offset Visualizer Component Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Cents Offset</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="cents-slider"
                                >Cents Off: {{ centsOff }}</label
                            >
                            <input
                                id="cents-slider"
                                type="range"
                                v-model.number="centsOff"
                                min="-100"
                                max="100"
                                step="1"
                            />
                            <div class="note-hint text-caption text-medium-emphasis">
                                Adjust the pitch offset in cents (-100 to +100)
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Quick Presets</h3>
                    <div class="property-group">
                        <div class="button-group">
                            <button
                                v-for="preset in presets"
                                :key="preset.label"
                                @click="centsOff = preset.value"
                                :class="{ active: centsOff === preset.value }"
                                class="state-btn"
                            >
                                {{ preset.label }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Variant</h3>
                    <div class="property-group">
                        <div class="button-group">
                            <button
                                @click="variant = 'default'"
                                :class="{ active: variant === 'default' }"
                                class="state-btn"
                            >
                                Default
                            </button>
                            <button
                                @click="variant = 'minimal'"
                                :class="{ active: variant === 'minimal' }"
                                class="state-btn"
                            >
                                Minimal
                            </button>
                        </div>
                        <div class="note-hint text-caption text-medium-emphasis">
                            <strong>Default:</strong> Full visual with gradient, bars, labels<br>
                            <strong>Minimal:</strong> Discrete style with dots, no labels, subtle background
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Orientation</h3>
                    <div class="property-group">
                        <div class="button-group">
                            <button
                                @click="orientation = 'horizontal'"
                                :class="{ active: orientation === 'horizontal' }"
                                class="state-btn"
                            >
                                Horizontal
                            </button>
                            <button
                                @click="orientation = 'vertical'"
                                :class="{ active: orientation === 'vertical' }"
                                class="state-btn"
                            >
                                Vertical
                            </button>
                        </div>
                        <div class="note-hint text-caption text-medium-emphasis">
                            <strong>Horizontal:</strong> Like a guitar tuner, left/right movement<br>
                            <strong>Vertical:</strong> Like notes on a staff, up/down pitch
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Max Display Range</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="max-display-slider"
                                >Max Display Cents: {{ maxDisplayCents }}</label
                            >
                            <input
                                id="max-display-slider"
                                type="range"
                                v-model.number="maxDisplayCents"
                                min="25"
                                max="100"
                                step="5"
                            />
                            <div class="note-hint text-caption text-medium-emphasis">
                                Maximum cents deviation shown on the visualizer
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Display Options</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showDetected"
                                />
                                Show "You" marker
                            </label>
                            <div class="note-hint text-caption text-medium-emphasis">
                                Toggle the detected pitch indicator (with smooth fade transition)
                            </div>
                        </div>
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showCents"
                                />
                                Show cents label
                            </label>
                            <div class="note-hint text-caption text-medium-emphasis">
                                Toggle the numeric cents offset display below the visualizer
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Animation Controls</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <button
                                @click="toggleAnimation"
                                class="state-btn"
                                :class="{ active: animationRunning }"
                            >
                                {{ animationRunning ? 'Stop Animation' : 'Start Animation' }}
                            </button>
                            <div class="note-hint text-caption text-medium-emphasis">
                                Animate through different cent values
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Live Preview</h2>
                <div class="preview-box">
                    <PitchOffsetVisualizer
                        :cents-off="centsOff"
                        :max-display-cents="maxDisplayCents"
                        :orientation="orientation"
                        :variant="variant"
                        :show-detected="showDetected"
                        :show-cents="showCents"
                    />
                </div>

                <v-card class="preview-info">
                    <v-card-title class="text-h6">Current Props</v-card-title>
                    <v-card-text>
                        <div class="prop-display">
                            <div><strong>centsOff:</strong> {{ centsOff }}</div>
                            <div><strong>maxDisplayCents:</strong> {{ maxDisplayCents }}</div>
                            <div><strong>orientation:</strong> "{{ orientation }}"</div>
                            <div><strong>variant:</strong> "{{ variant }}"</div>
                            <div><strong>showDetected:</strong> {{ showDetected }}</div>
                            <div><strong>showCents:</strong> {{ showCents }}</div>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card class="interpretation-info">
                    <v-card-title class="text-h6">Interpretation</v-card-title>
                    <v-card-text>
                        <div class="interpretation">
                            <div class="status-badge" :class="statusClass">
                                {{ statusText }}
                            </div>
                            <div class="interpretation-text">
                                {{ interpretationText }}
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Code Output -->
        <v-card class="code-section">
            <v-card-title>
                <div class="code-header">
                    <h2 class="text-h6">Component Code</h2>
                    <v-btn @click="copyCode" size="small" variant="outlined">
                        📋 Copy
                    </v-btn>
                </div>
            </v-card-title>
            <v-card-text>
                <pre><code>{{ componentCode }}</code></pre>
            </v-card-text>
        </v-card>

        <!-- API Documentation -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                API Documentation
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">Props</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Prop</th>
                            <th>Type</th>
                            <th>Default</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>centsOff</code></td>
                            <td><code>number</code></td>
                            <td>required</td>
                            <td>The pitch offset in cents (positive = sharp, negative = flat)</td>
                        </tr>
                        <tr>
                            <td><code>maxDisplayCents</code></td>
                            <td><code>number</code></td>
                            <td>50</td>
                            <td>Maximum cents deviation to display (values beyond this are clamped)</td>
                        </tr>
                        <tr>
                            <td><code>orientation</code></td>
                            <td><code>"horizontal" | "vertical"</code></td>
                            <td>"horizontal"</td>
                            <td>Layout direction: horizontal (like guitar tuner) or vertical (like staff notation)</td>
                        </tr>
                        <tr>
                            <td><code>variant</code></td>
                            <td><code>"default" | "minimal"</code></td>
                            <td>"default"</td>
                            <td>Visual style: default (gradient, bars, labels) or minimal (subtle, dots, no labels)</td>
                        </tr>
                        <tr>
                            <td><code>showDetected</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>Whether to show the "You" marker (fades in/out with transition)</td>
                        </tr>
                        <tr>
                            <td><code>showCents</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>Whether to show the numeric cents offset label below the visualizer</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">Features</h3>
                <ul class="api-list">
                    <li><strong>Visual feedback:</strong> Shows target and detected pitch positions</li>
                    <li><strong>Color-coded background:</strong> Green center (in tune), yellow edges (slightly off), red far edges (very off)</li>
                    <li><strong>Smooth animation:</strong> Detected pitch marker smoothly transitions between positions</li>
                    <li><strong>Cent display:</strong> Shows numeric cent offset with sign (+/-)</li>
                    <li><strong>Clamping:</strong> Values beyond maxDisplayCents are clamped to keep visualization readable</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Visual Guide</h3>
                <ul class="api-list">
                    <li><strong>Blue marker (Target):</strong> The ideal pitch position</li>
                    <li><strong>Green marker (You):</strong> Your detected pitch position</li>
                    <li><strong>Horizontal mode:</strong> Moves left (flat) or right (sharp) relative to target</li>
                    <li><strong>Vertical mode:</strong> Moves down (flat) or up (sharp) relative to target</li>
                    <li><strong>Background gradient:</strong> Visual aid showing tuning zones (green = in tune, yellow = slightly off, red = very off)</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Orientation Context</h3>
                <ul class="api-list">
                    <li><strong>Horizontal:</strong> Familiar for guitar/instrument tuners, natural for inline feedback, compact vertical footprint</li>
                    <li><strong>Vertical:</strong> Matches musical staff notation (higher pitch = higher position), reinforces "high/low" pitch vocabulary, works well adjacent to staff displays</li>
                </ul>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import PitchOffsetVisualizer from "../components/PitchOffsetVisualizer.vue";

const centsOff = ref(0);
const maxDisplayCents = ref(50);
const orientation = ref<"horizontal" | "vertical">("horizontal");
const variant = ref<"default" | "minimal">("default");
const showDetected = ref(true);
const showCents = ref(true);
const animationRunning = ref(false);
let animationInterval: number | null = null;

const presets = [
    { label: "Perfect (0¢)", value: 0 },
    { label: "Slightly Sharp (+5¢)", value: 5 },
    { label: "Slightly Flat (-5¢)", value: -5 },
    { label: "Sharp (+15¢)", value: 15 },
    { label: "Flat (-15¢)", value: -15 },
    { label: "Very Sharp (+30¢)", value: 30 },
    { label: "Very Flat (-30¢)", value: -30 },
    { label: "Extremely Sharp (+50¢)", value: 50 },
    { label: "Extremely Flat (-50¢)", value: -50 },
];

const statusClass = computed(() => {
    const absCents = Math.abs(centsOff.value);
    if (absCents <= 5) return "status-perfect";
    if (absCents <= 15) return "status-good";
    if (absCents <= 30) return "status-fair";
    return "status-poor";
});

const statusText = computed(() => {
    const absCents = Math.abs(centsOff.value);
    if (absCents <= 5) return "Perfect";
    if (absCents <= 15) return "Good";
    if (absCents <= 30) return "Fair";
    return "Needs Adjustment";
});

const interpretationText = computed(() => {
    if (centsOff.value === 0) {
        return "Perfect pitch! The detected note exactly matches the target.";
    }
    const direction = centsOff.value > 0 ? "sharp" : "flat";
    const absCents = Math.abs(centsOff.value);

    if (absCents <= 5) {
        return `Very slightly ${direction}. Most listeners won't notice this deviation.`;
    }
    if (absCents <= 15) {
        return `Slightly ${direction}. This is within acceptable tuning tolerance for most contexts.`;
    }
    if (absCents <= 30) {
        return `Noticeably ${direction}. Adjustment recommended for better tuning.`;
    }
    return `Significantly ${direction}. Immediate adjustment needed.`;
});

const componentCode = computed(() => {
    const props: string[] = [];

    props.push(`  :cents-off="${centsOff.value}"`);

    if (maxDisplayCents.value !== 50) {
        props.push(`  :max-display-cents="${maxDisplayCents.value}"`);
    }

    if (orientation.value !== "horizontal") {
        props.push(`  orientation="${orientation.value}"`);
    }

    if (variant.value !== "default") {
        props.push(`  variant="${variant.value}"`);
    }

    if (!showDetected.value) {
        props.push(`  :show-detected="false"`);
    }

    if (!showCents.value) {
        props.push(`  :show-cents="false"`);
    }

    const propsString = props.length > 0 ? "\n" + props.join("\n") + "\n" : "";

    return `<PitchOffsetVisualizer${propsString}/>`;
});

function toggleAnimation() {
    if (animationRunning.value) {
        stopAnimation();
    } else {
        startAnimation();
    }
}

function startAnimation() {
    animationRunning.value = true;
    let direction = 1;
    let currentValue = 0;

    animationInterval = window.setInterval(() => {
        currentValue += direction * 2;
        if (currentValue >= 50 || currentValue <= -50) {
            direction *= -1;
        }
        centsOff.value = currentValue;
    }, 100);
}

function stopAnimation() {
    animationRunning.value = false;
    if (animationInterval !== null) {
        clearInterval(animationInterval);
        animationInterval = null;
    }
}

async function copyCode() {
    try {
        await navigator.clipboard.writeText(componentCode.value);
        alert("Code copied to clipboard!");
    } catch (err) {
        console.error("Failed to copy:", err);
    }
}

onUnmounted(() => {
    stopAnimation();
});
</script>

<style scoped>
.debug-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 30px;
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.checkbox-label {
    display: flex !important;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
    margin: 0;
    cursor: pointer;
}

.property-control input[type="range"] {
    width: 100%;
}

.note-hint {
    font-size: 12px;
    font-style: italic;
    opacity: 0.7;
    margin-top: 8px;
}

.button-group {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.state-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    text-align: center;
    font-weight: 500;
}

.state-btn:hover {
    opacity: 0.8;
}

.state-btn.active {
    font-weight: bold;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
    border-radius: 6px;
    min-height: 200px;
    background: rgba(0, 0, 0, 0.02);
}

.preview-info {
    margin-top: 20px;
}

.interpretation-info {
    margin-top: 20px;
}

.interpretation {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.status-badge {
    display: inline-block;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    align-self: flex-start;
}

.status-badge.status-perfect {
    background: #28a745;
    color: white;
}

.status-badge.status-good {
    background: #8bc34a;
    color: white;
}

.status-badge.status-fair {
    background: #ffc107;
    color: #333;
}

.status-badge.status-poor {
    background: #dc3545;
    color: white;
}

.interpretation-text {
    font-size: 14px;
    line-height: 1.6;
    color: #495057;
}

.prop-display div {
    margin-bottom: 5px;
    font-family: monospace;
    font-size: 14px;
    padding: 8px;
    border-radius: 3px;
    font-weight: 500;
}

.code-section {
    margin-top: 40px;
}

.code-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.code-section pre {
    margin: 0;
    overflow-x: auto;
    padding: 15px;
    border-radius: 4px;
}

.code-section code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
